import { z } from 'zod';

import {
  IS_REQUIRED,
  isGreaterThanOrEqualTo,
  MINIMUM_ITEMS_ERROR_MESSAGE_TEXT,
} from '@occc-app/lib/constants/validationMessages';
import { Count_Products_By, LocationGroupFragment } from '@occc/graphql';
import {
  futureFixedWindowSchema,
  timeWindowSchema,
} from '@occc-app/routes/Audience/validation/time-period.validation';

import { hasFilledValuesInLoyaltyEventForm, hasFilledValuesInPurchaseForm } from './utils';

import { QualifiedEventsMemberPurchasedOperator } from './constants';
import { QualifiedEventsFormValues, QualifiedEventType, TimePeriod } from '../../types';

const getIsPurchaseFormHasFilledValues = (values: QualifiedEventsFormValues) => {
  return values?.event === QualifiedEventType.MadePurchase && hasFilledValuesInPurchaseForm(values);
};

const getIsLoyaltyEventHasFilledValues = (values: QualifiedEventsFormValues) => {
  return (
    values?.event === QualifiedEventType.FulfilledLoyaltyEvent &&
    hasFilledValuesInLoyaltyEventForm(values)
  );
};

const zodPurchaseCommonSchema = z.object({
  event: z.literal(QualifiedEventType.MadePurchase, {
    required_error: IS_REQUIRED,
    invalid_type_error: IS_REQUIRED,
  }),
  times: z.number({ required_error: IS_REQUIRED }).min(1, isGreaterThanOrEqualTo(1)),
  productGroups: z.array(z.object({ id: z.string() })).min(1, MINIMUM_ITEMS_ERROR_MESSAGE_TEXT),
  countProductsBy: z.nativeEnum(Count_Products_By).refine((val) => val !== undefined, {
    message: IS_REQUIRED,
  }),
  threshold: z.number({ required_error: IS_REQUIRED }).min(1, isGreaterThanOrEqualTo(1)),
  locations: z.array(z.object({ uuid: z.string() })).optional(),
  locationGroups: z.array(z.custom<LocationGroupFragment>()).optional(),
  timePeriodType: z.nativeEnum(TimePeriod),
  operator: z.nativeEnum(QualifiedEventsMemberPurchasedOperator),
});

export const purchasesSchema = z.array(
  z.discriminatedUnion('timePeriodType', [
    zodPurchaseCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.PreviousMonth),
    }),
    zodPurchaseCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.RollingWindow),
      timeWindow: timeWindowSchema,
    }),
    zodPurchaseCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.FixedWindow),
      fixedWindow: futureFixedWindowSchema,
    }),
  ]),
);

const zodFulfilledLoyaltyEventCommonSchema = z.object({
  event: z.literal(QualifiedEventType.FulfilledLoyaltyEvent, {
    required_error: IS_REQUIRED,
    invalid_type_error: IS_REQUIRED,
  }),
  times: z.number({ required_error: IS_REQUIRED }).min(1, isGreaterThanOrEqualTo(1)),
  loyaltyEvent: z.object({ id: z.string() }, { required_error: IS_REQUIRED }),
});

export const fulfilledLoyaltyEventSchema = z.array(
  z.discriminatedUnion('timePeriodType', [
    zodFulfilledLoyaltyEventCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.PreviousMonth),
    }),
    zodFulfilledLoyaltyEventCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.RollingWindow),
      timeWindow: timeWindowSchema,
    }),
    zodFulfilledLoyaltyEventCommonSchema.extend({
      timePeriodType: z.literal(TimePeriod.FixedWindow),
      fixedWindow: futureFixedWindowSchema,
    }),
  ]),
);

const getAddZodIssue = (ctx: z.RefinementCtx) => (issue: z.ZodIssue) => {
  ctx.addIssue({
    ...issue,
    path: issue.path,
  });
};

export const qualifiedEventsSchema = z
  .array(
    z.custom<QualifiedEventsFormValues>().superRefine((values, ctx) => {
      if (getIsPurchaseFormHasFilledValues(values)) {
        const result = purchasesSchema.element.safeParse(values);
        if (result.success) return;
        return result.error.issues.forEach(getAddZodIssue(ctx));
      }

      if (getIsLoyaltyEventHasFilledValues(values)) {
        const result = fulfilledLoyaltyEventSchema.element.safeParse(values);
        if (result.success) return;
        return result.error.issues.forEach(getAddZodIssue(ctx));
      }
    }),
  )
  .optional();
